import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import './TourRedirect.css';

const APP_STORE_URL = 'https://play.google.com/store/apps/details?id=com.habeebbuilds.rentahome';

function TourRedirect() {
    const { id } = useParams();
    const [tour, setTour] = useState(null);
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const loadTour = async () => {
            setLoading(true);
            setError(null);

            try {
                const { data: reelData, error: reelError } = await supabase
                    .from('reels')
                    .select('id, property_id, video_url, thumbnail_url, description, created_at')
                    .eq('id', id)
                    .maybeSingle();

                if (reelError) throw reelError;
                if (!reelData) {
                    throw new Error('Tour not found');
                }

                const { data: propertyData, error: propertyError } = await supabase
                    .from('property')
                    .select('id, title, bhk, property_type, expected_rent, address_line, status, media_url, areas(name, city)')
                    .eq('id', reelData.property_id)
                    .maybeSingle();

                if (propertyError) throw propertyError;

                if (isMounted) {
                    setTour(reelData);
                    setProperty(propertyData || null);
                }
            } catch (err) {
                console.error('Error loading shared tour:', err);
                if (isMounted) {
                    setError('This tour could not be loaded.');
                    setTour(null);
                    setProperty(null);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadTour();

        return () => {
            isMounted = false;
        };
    }, [id]);

    useEffect(() => {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent);
        if (!isMobile || !id) return undefined;

        const deepLink = `rentahome://therentcompany.com/tours/${id}`;
        const attemptTimer = window.setTimeout(() => {
            window.location.assign(deepLink);
        }, 350);

        return () => window.clearTimeout(attemptTimer);
    }, [id]);

    const appDeepLink = useMemo(
        () => `rentahome://therentcompany.com/tours/${id}`,
        [id],
    );

    const sharePath = `/tours/${id}`;
    const propertyTitle = property?.title
        || (property
            ? `${property.bhk || ''} ${property.property_type || 'Property'}`
                .replace(/\s+/g, ' ')
                .trim()
            : 'Property Tour');
    const propertyLocation = property?.areas
        ? `${property.areas.name}, ${property.areas.city}`
        : 'Bangalore';
    const pageTitle = `${propertyTitle} Tour in ${propertyLocation}`;
    const pageDescription = tour?.description
        || `Watch this property tour on The Rent Company and explore the space before you visit.`;
    const previewImage = tour?.thumbnail_url || property?.media_url?.[0] || null;

    return (
        <div className="tour-page">
            <SEO
                title={pageTitle}
                description={pageDescription}
                image={previewImage}
                path={sharePath}
            />

            <Navbar />

            <main className="tour-main">
                <div className="tour-shell">
                    {loading ? (
                        <section className="tour-card tour-state-card">
                            <div className="tour-skeleton tour-skeleton-video"></div>
                            <div className="tour-skeleton tour-skeleton-title"></div>
                            <div className="tour-skeleton tour-skeleton-text"></div>
                        </section>
                    ) : error || !tour ? (
                        <section className="tour-card tour-state-card">
                            <p className="tour-eyebrow">Shared Property Tour</p>
                            <h1>Tour unavailable</h1>
                            <p className="tour-state-copy">
                                The shared link is no longer available or may have been removed.
                            </p>
                            <div className="tour-actions">
                                <a className="tour-btn tour-btn-primary" href={APP_STORE_URL}>
                                    Download the app
                                </a>
                                <Link className="tour-btn tour-btn-secondary" to="/">
                                    Browse properties
                                </Link>
                            </div>
                        </section>
                    ) : (
                        <section className="tour-card">
                            <div className="tour-hero">
                                <div className="tour-video-frame">
                                    <video
                                        className="tour-video"
                                        controls
                                        playsInline
                                        autoPlay
                                        muted
                                        preload="metadata"
                                        poster={tour.thumbnail_url || property?.media_url?.[0] || undefined}
                                    >
                                        <source src={tour.video_url} type="video/mp4" />
                                        Your browser does not support HTML5 video.
                                    </video>
                                </div>

                                <div className="tour-summary">
                                    <p className="tour-eyebrow">Shared Property Tour</p>
                                    <h1>{propertyTitle}</h1>
                                    <p className="tour-location">{propertyLocation}</p>

                                    {tour.description && (
                                        <p className="tour-description">{tour.description}</p>
                                    )}

                                    <div className="tour-meta-row">
                                        {property?.expected_rent ? (
                                            <div className="tour-meta-pill">
                                                Rent: Rs. {Number(property.expected_rent).toLocaleString()}/month
                                            </div>
                                        ) : null}
                                        {property?.status ? (
                                            <div className="tour-meta-pill">
                                                Status: {property.status}
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="tour-actions">
                                        <a className="tour-btn tour-btn-primary" href={appDeepLink}>
                                            Open in app
                                        </a>
                                        <a className="tour-btn tour-btn-secondary" href={APP_STORE_URL}>
                                            Get the app
                                        </a>
                                        {property?.id ? (
                                            <Link className="tour-btn tour-btn-ghost" to={`/properties/${property.id}`}>
                                                View property details
                                            </Link>
                                        ) : null}
                                    </div>

                                    <p className="tour-hint">
                                        If you cancel the app prompt or do not have the app installed, you can keep watching the tour right here on the website.
                                    </p>
                                </div>
                            </div>

                            {property && (
                                <section className="tour-property-panel">
                                    <div>
                                        <p className="tour-panel-label">Address</p>
                                        <p className="tour-panel-value">
                                            {[property.address_line, propertyLocation].filter(Boolean).join(', ')}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="tour-panel-label">Property</p>
                                        <p className="tour-panel-value">
                                            {[property.bhk, property.property_type].filter(Boolean).join(' ')}
                                        </p>
                                    </div>
                                </section>
                            )}
                        </section>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
}

export default TourRedirect;
